import { Container, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import styles from "./MortgageCalculator.module.css";
import Grid from "@mui/material/Grid";
import mortgage_calc_icon from "assets/images/mortgage-calc-icon.svg";
import { useForm } from "react-hook-form";
import Range from "component/range/Range";
import TextField from "component/textField/TextField";
import AppButton from "component/button/AppButton";
import { useMortgageCalc } from "api/mortgageCalc";
import { monthsToYears, numberWithCommas } from "utils/helpers";
import { yupResolver } from "@hookform/resolvers/yup";

import ValidationMessage from "component/validationMessage/ValidationMessage";
import { mortgageCalcSchema } from "schemas/mortgageCalc/MortgageCalcSchema";
import AppDatePicker from "component/inputs/datePicker/DatePicker";

function MortgageCalculator() {
  const [equityContribution, setEquityContribution] = useState(0);
  const pageRef = useRef(null);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(mortgageCalcSchema),
    defaultValues: { interestRate: "28.50" },
  });

  const [tenorMessage, setTenorMessage] = useState(null);
  const [propertyPrice, setPropertyPrice] = useState(null);
  const [propertyValue, setPropertyValue] = useState(null);
  const DOB = watch('dob')
  useEffect(() => {
    if (!!propertyPrice && !!propertyValue) {
      setEquityContribution(propertyValue * 0.2);
    }
  }, [propertyPrice, propertyValue]);

  useEffect(() => {
    if (!!DOB) {
      const dobDate = new Date(DOB);
      const today = new Date(Date.now());
      const age = today.getFullYear() - dobDate.getFullYear();

      let timeLeft =
        60 * 12 + today.getMonth() - (age * 12 + dobDate.getMonth());

      if (timeLeft < -720) {
        setTenorMessage(null);
      } else if (timeLeft > 720) {
        setTenorMessage(
          "Enter a valid Date of birth to see the maximum tenor allowed"
        );
      } else if (timeLeft < 1) {
        setTenorMessage("You are past the age for a mortgage");
      } else if (timeLeft >= 1 && timeLeft <= 720) {
        setTenorMessage(
          `NB: Maximum tenor allowed for you is ${timeLeft} months`
        );
      }
    }
  }, [DOB]);



  const { data: mortgageResp, isLoading, mutate } = useMortgageCalc();

  const submitForm = (data) => {
    let equityContributionValue;
    if (typeof equityContribution === "number") {
      equityContributionValue = equityContribution;
    } else equityContributionValue = parseInt(equityContribution);
    const payload = { ...data, equityContribution: equityContributionValue };
    mutate(payload);
  };

  useEffect(() => {
    if (typeof window !== "undefined" && mortgageResp?.status) {
      if (window?.innerWidth > 900) {
        pageRef.current.scrollIntoView({
          behaviour: "smooth",
          block: "start",
        });
      } else
        pageRef.current.scrollIntoView({ behaviour: "smooth", block: "end" });
    }
  }, [mortgageResp]);

  return (
    <div ref={pageRef}>
      <Grid container rowGap="6" justifyContent={["center", , "start"]}>
        <Grid xs={12} lg={8} justifyContent={["center", , "start"]}>
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={styles.mortgage_calculator}
          >
            <div className={styles.heading}>
              <Typography>Mortgage calculator</Typography>
              <img src={mortgage_calc_icon} alt="" />
            </div>
            <div className={styles.body}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <div className={styles.field}>
                    <TextField
                      name="propertyValue"
                      errors={errors}
                      register={register}
                      type="text"
                      placeholder="10,000,000"
                      label="Property Value"
                      onChange={(e) => setPropertyValue(e.target.value)}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={styles.field}>
                    <TextField
                      name="propertyPrice"
                      errors={errors}
                      register={register}
                      type="text"
                      placeholder="10,000,000"
                      label="Property Price"
                      onChange={(e) => setPropertyPrice(e.target.value)}
                    />
                  </div>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <div className={styles.field}>
                    <Range
                      equityContribution={equityContribution}
                      setEquityContribution={setEquityContribution}
                      propertyPrice={propertyPrice}
                      propertyValue={propertyValue}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={styles.field}>
                    <AppDatePicker
                      errors={errors}
                      name="dob"
                      label="Date of Birth"
                      setValue={setValue}
                      value={DOB}
                      trigger={trigger}
                    />
                    {/* <TextField
                      name="dob"
                      errors={errors}
                      register={register}
                      type="date"
                      placeholder="10,000,000"
                      label="Date of Birth"
                      onChange={(e) => setDOB(e.target.value)}
                    /> */}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={styles.field}>
                    <TextField
                      name="netSalary"
                      errors={errors}
                      register={register}
                      type="text"
                      placeholder="10,000,000"
                      label="Net Salary"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={styles.field}>
                    <TextField
                      name="interestRate"
                      errors={errors}
                      register={register}
                      type="text"
                      placeholder="28.50%"
                      label="Interest Rate"
                      disabled
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={styles.field}>
                    <TextField
                      name="tenor"
                      errors={errors}
                      register={register}
                      type="text"
                      placeholder="......."
                      label="Tenor (Months)"
                    />
                    {!!DOB && DOB?.length > 0 && (
                      <ValidationMessage message={tenorMessage} />
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={12}>
                  <div className={styles.btn}>
                    <AppButton
                      width="149px"
                      btnText="Submit"
                      textColor="white"
                      btnBg="#0089FF"
                      loading={isLoading}
                      handleAction={handleSubmit(submitForm)}
                    />
                  </div>
                </Grid>
              </Grid>
            </div>
          </div>
        </Grid>

        {!!mortgageResp && (
          // mortgageResp?.data?.computed?.repayments?.length > 0 &&
          <Grid xs={12} lg={4}>
            <MortgageSummary mortgageDetails={mortgageResp?.data?.computed} />
          </Grid>
        )}
      </Grid>
    </div>
  );
}

export default MortgageCalculator;

const MortgageSummary = ({ mortgageDetails }) => {
  return (
    <Container>
      <div className={styles.summary}>
        <div className={styles.amount}>
          <Typography variant="h3">Amount:</Typography>
          <Typography variant="h2">
            ₦ {numberWithCommas(mortgageDetails?.loanAmount)}
          </Typography>
        </div>

        <div className={styles.more}>
          <div>
            <Typography variant="h5">Tenor</Typography>
            <Typography>{monthsToYears(mortgageDetails?.tenor)}</Typography>
          </div>
          <div>
            <Typography variant="h5">Max Monthly Repayment:</Typography>
            <Typography>
              ₦ {numberWithCommas(mortgageDetails?.repayments[0]?.repayment)}
            </Typography>
          </div>
        </div>
      </div>
    </Container>
  );
};